import {
  Divider,
  Stack,
  Box,
  Button,
  Typography,
  SvgIcon
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { Center } from "../center/Center";
import SvgRobot20Px from "../icons/autostoreIcons/icons20px/Robot20Px";
import SvgError24Px from "../icons/autostoreIcons/icons24px/Error24Px";

type Props = {
  errorCode?: number;
  errorMessage?: string;
  goBackLocation?: string;
};

/**
 * A page that renders an error and an error code.
 *
 * Recommended usage is at the top level of an app in place of a page that has
 * failed to load or has thrown an error.
 */
export function ErrorPage({
  errorCode = 404,
  errorMessage,
  goBackLocation
}: Props) {
  const { t } = useTranslation();

  return (
    <Center>
      <Stack spacing={8} marginTop="20vh">
        <Stack
          direction="row"
          justifyContent="start"
          alignItems="center"
          gap={6}
        >
          <SvgIcon
            component={SvgError24Px}
            sx={{
              fontSize: 64,
              color: "error.main",
              marginLeft: 2
            }}
          />
          <Typography variant="h1">{errorCode}</Typography>
        </Stack>
        <Box>
          <Center>
            <Typography>{t("oops there's an error")}</Typography>
            <Typography>
              {t("please either refresh the page or return home to try again")}
            </Typography>
          </Center>
        </Box>
        {goBackLocation && (
          <Button
            variant="contained"
            href={goBackLocation}
            startIcon={<SvgIcon component={SvgRobot20Px} />}
          >
            {t("get back on track")}
          </Button>
        )}
        {errorMessage && (
          <Stack>
            <Divider />
            <Typography variant="h5">{t("error details")}</Typography>
            <Typography variant="caption" component="pre">
              {errorMessage}
            </Typography>
          </Stack>
        )}
      </Stack>
    </Center>
  );
}
